import React from 'react';

interface BudgetAlert {
  id: string;
  alert_type: string;
  message: string;
  current_spend: number;
  budget_amount: number;
  threshold_percentage: number;
}

interface BudgetAlertsProps {
  alerts: BudgetAlert[];
}

const BudgetAlerts: React.FC<BudgetAlertsProps> = ({ alerts }) => {
  if (!alerts || alerts.length === 0) {
    return null;
  }

  const getAlertIcon = (alertType: string) => {
    if (alertType === 'critical') {
      return (
        <div className="flex-shrink-0 bg-red-100 dark:bg-red-900/50 rounded-full p-2">
          <svg
            className="h-6 w-6 text-red-600 dark:text-red-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
      );
    }

    return (
      <div className="flex-shrink-0 bg-yellow-100 dark:bg-yellow-900/50 rounded-full p-2">
        <svg
          className="h-6 w-6 text-yellow-600 dark:text-yellow-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </div>
    );
  };

  const getAlertBgColor = (alertType: string) => {
    if (alertType === 'critical') {
      return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
    }
    return 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800';
  };

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Budget Alerts</h2>
      {alerts.map((alert) => (
        <div
          key={alert.id}
          className={`${getAlertBgColor(alert.alert_type)} border rounded-lg p-4`}
        >
          <div className="flex items-start">
            {getAlertIcon(alert.alert_type)}
            <div className="ml-4 flex-1">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                {alert.message}
              </h3>
              <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                <p>
                  Current spend: ${alert.current_spend.toLocaleString()} / ${alert.budget_amount.toLocaleString()}
                  ({alert.threshold_percentage}% threshold exceeded)
                </p>
              </div>
              <button className="mt-3 text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline">
                View Details
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default BudgetAlerts;
